import { useCallback } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { trackError } from '~/services/logger/error.utils';

interface IUseStorageProps {
    key: string;
    path?: string;
    storage?: Storage;
}

export function setValueToStorage({
    key,
    path,
    storage = localStorage,
    value
}: IUseStorageProps & { value: any }) {
    const rootItem = storage.getItem(key);
    let rootObject = rootItem ? JSON.parse(rootItem) : {};
    if (path) {
        set(rootObject, path, value);
    } else {
        rootObject = value;
    }
    try {
        storage.setItem(key, JSON.stringify(rootObject));
    } catch (error) {
        trackError({
            error,
            message: 'Error setting data to storage',
            context: {
                content: JSON.stringify(rootObject)
            }
        });
    }
}

export function getValueFromStorage({
    key,
    path,
    storage = localStorage
}: IUseStorageProps) {
    const rootItem = storage.getItem(key);
    const parsedRootItem =
        rootItem && rootItem !== 'undefined' ? JSON.parse(rootItem) : {};
    if (path) {
        return get(parsedRootItem, path);
    }
    return parsedRootItem ?? {};
}

export function deleteValueFromStorage({
    key,
    path,
    storage = localStorage
}: IUseStorageProps) {
    try {
        // no path - delete whole key
        if (!path) {
            storage.removeItem(key);
            return true;
        }

        const rootItem = getValueFromStorage({ key, storage });

        const isMultiPath = (_path: string) => _path.includes('.');

        const deletePath = (pathToRemove: string) => {
            unset(rootItem, pathToRemove);

            if (isMultiPath(pathToRemove)) {
                const parentPath = pathToRemove.slice(
                    0,
                    pathToRemove.lastIndexOf('.')
                );
                const parentValue = get(rootItem, parentPath);
                const isEmptyParent =
                    !parentValue || Object.keys(parentValue).length === 0;
                if (isEmptyParent) {
                    deletePath(parentPath);
                }
            }
        };
        deletePath(path);

        // if rootItem is empty - delete whole key
        if (Object.keys(rootItem).length === 0) {
            storage.removeItem(key);
        } else {
            // update result
            storage.setItem(key, JSON.stringify(rootItem));
        }
        return true;
    } catch (error) {
        trackError({ error });
        return false;
    }
}

export const useStorage = <T = any>({
    initialValue,
    key,
    path,
    storage = localStorage
}: IUseStorageProps & { initialValue?: T }) => {
    const setValue = useCallback(
        (value: T) => {
            try {
                setValueToStorage({ key, path, storage, value });
            } catch (error) {
                trackError({ error });
            }
        },
        [key, path, storage]
    );

    const deleteValue = useCallback(() => {
        return deleteValueFromStorage({ key, path, storage });
    }, [key, path, storage]);

    const getValue = useCallback((): T => {
        try {
            return getValueFromStorage({ key, path, storage }) ?? initialValue;
        } catch (error) {
            trackError({ error });
            return initialValue;
        }
    }, [initialValue, key, path, storage]);

    if (!key) {
        return {};
    }

    const storedValue = getValue();

    return { storedValue, setValue, deleteValue, getValue };
};
